import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, X } from 'lucide-react';
import { useTaskDrafts } from '@/hooks/useVoiceTaskDrafts';
import { TaskDraftList } from '@/components/ai/TaskDraftList';

// "Make tasks" panel under an assistant answer: AI extracts 1-5 drafts, user reviews, then creates.
export const AnswerTasks = ({ answer, teamId, teams, user, onClose, messageId }) => {
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [createdCount, setCreatedCount] = useState(0);
  const capture = useTaskDrafts({ teamId, onError: setError, descriptionSuffix: 'Source: Vibely AI chat' });
  const { requestDrafts, processing, drafts } = capture;
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    requestDrafts({ text: answer }).then(() => setLoaded(true));
  }, [requestDrafts, answer]);

  // Everything discarded without creating anything → collapse the panel.
  useEffect(() => {
    if (loaded && !processing && !error && drafts.length === 0 && createdCount === 0) onClose();
  }, [loaded, processing, error, drafts.length, createdCount, onClose]);

  const finished = createdCount > 0 && drafts.length === 0 && !processing;

  return (
    <div className="chat-answer-tasks" data-testid={`answer-tasks-${messageId}`}>
      {processing && (
        <div className="chat-answer-tasks-status" data-testid="answer-tasks-loading">
          <Loader2 size={14} className="animate-spin" /> Finding tasks in this answer…
        </div>
      )}
      {error && !processing && (
        <div className="chat-answer-tasks-status error" role="alert" data-testid="answer-tasks-error">
          <span>{error}</span>
          <button type="button" className="chat-action" onClick={onClose} aria-label="Dismiss" data-testid="answer-tasks-dismiss"><X size={13} /></button>
        </div>
      )}
      {finished && (
        <div className="chat-created-note" data-testid="answer-tasks-created">
          <CheckCircle2 size={14} /> {createdCount} task{createdCount === 1 ? '' : 's'} created ·{' '}
          <Link to="/dashboard/kanban" data-testid="answer-tasks-open-kanban">Open Kanban</Link>
        </div>
      )}
      {drafts.length > 0 && (
        <TaskDraftList
          capture={capture}
          teams={teams}
          user={user}
          compact
          showTranscript={false}
          announce={false}
          onCreated={() => setCreatedCount((n) => n + 1)}
        />
      )}
    </div>
  );
};
